<template>
  <div v-if="isVisible" class="filter-overlay">
    <div class="filter-overlay-backdrop" @click="close"></div>
    <div class="filter-overlay-content">
      <div class="filter-header">
        <h2>Add Device :</h2>
      </div>

      <!-- Carousel/Slider navigation -->
      <div class="carousel-navigation">
        <div
            v-for="(step, index) in steps"
            :key="index"
            :class="['step-indicator', { 'active': currentStep === index, 'completed': currentStep > index }]"
        >
          <div class="step-number">{{ index + 1 }}</div>
          <div class="step-name">{{ step }}</div>
        </div>
      </div>

      <!-- Form Steps -->
      <div class="form-steps">
        <!-- Step 1: Device Type Selection -->
        <div v-if="currentStep === 0" class="form-step">
          <div class="form-group">
            <div class="filter-label-container">
              <label class="filter-label">Device Type</label>
              <div class="hint-icon" title="Select if you're adding a meter or an add-on module">?</div>
            </div>
            <div class="toggle-selection-wrapper">
              <button
                  :class="['toggle-option', { active: deviceData.deviceCategory === 'meter' }]"
                  @click="deviceData.deviceCategory = 'meter'"
              >
                Meter
              </button>
              <button
                  :class="['toggle-option', { active: deviceData.deviceCategory === 'addon' }]"
                  @click="deviceData.deviceCategory = 'addon'"
              >
                Add-on Module
              </button>
            </div>
          </div>

          <div v-if="deviceData.deviceCategory === 'meter'" class="form-group">
            <div class="filter-label-container">
              <label class="filter-label">Is Integrated</label>
              <div class="hint-icon" title="Select if this meter is an integrated smart meter">?</div>
            </div>
            <div class="toggle-selection-wrapper">
              <button
                  :class="['toggle-option', { active: deviceData.IsIntegrated === true }]"
                  @click="deviceData.IsIntegrated = true"
              >
                Yes
              </button>
              <button
                  :class="['toggle-option', { active: deviceData.IsIntegrated === false }]"
                  @click="deviceData.IsIntegrated = false"
              >
                No
              </button>
            </div>
          </div>

          <div v-if="deviceData.deviceCategory === 'meter' && deviceData.IsIntegrated" class="form-group">
            <div class="filter-label-container">
              <label class="filter-label">Protocol</label>
              <div class="hint-icon" title="Select the communication protocol for this integrated meter">?</div>
            </div>
            <div class="toggle-selection-wrapper">
              <button
                  :class="['toggle-option', { active: deviceData.Protocol === 'DLMS' }]"
                  @click="deviceData.Protocol = 'DLMS'"
              >
                DLMS
              </button>
              <button
                  :class="['toggle-option', { active: deviceData.Protocol === 'LORA_WBUS' }]"
                  @click="deviceData.Protocol = 'LORA_WBUS'"
              >
                LORA & WBUS
              </button>
              <button
                  :class="['toggle-option', { active: deviceData.Protocol === 'CTR' }]"
                  @click="deviceData.Protocol = 'CTR'"
              >
                CTR
              </button>
            </div>
          </div>

          <div v-if="deviceData.deviceCategory === 'addon'" class="form-group">
            <div class="filter-label-container">
              <label class="filter-label">Protocol</label>
              <div class="hint-icon" title="Select the communication protocol for this add-on module">?</div>
            </div>
            <div class="toggle-selection-wrapper">
              <button
                  :class="['toggle-option', { active: deviceData.Protocol === 'DLMS' }]"
                  @click="deviceData.Protocol = 'DLMS'"
              >
                DLMS
              </button>
              <button
                  :class="['toggle-option', { active: deviceData.Protocol === 'LORA' }]"
                  @click="deviceData.Protocol = 'LORA'"
              >
                LORA
              </button>
              <button
                  :class="['toggle-option', { active: deviceData.Protocol === 'WBUS' }]"
                  @click="deviceData.Protocol = 'WBUS'"
              >
                WBUS
              </button>
              <button
                  :class="['toggle-option', { active: deviceData.Protocol === 'LORA_WBUS' }]"
                  @click="deviceData.Protocol = 'LORA_WBUS'"
              >
                LORA & WBUS
              </button>
              <button
                  :class="['toggle-option', { active: deviceData.Protocol === 'CTR' }]"
                  @click="deviceData.Protocol = 'CTR'"
              >
                CTR
              </button>
            </div>
          </div>
        </div>

        <!-- Step 2: Basic Information -->
        <div v-if="currentStep === 1" class="form-step">
          <div class="form-group">
            <div class="filter-label-container">
              <label class="filter-label">Logical Device Number (LDN)</label>
              <span class="required-asterisk">*</span>
              <div class="hint-icon" title="Unique identifier for the device">?</div>
            </div>
            <div class="search-input-container">
              <input
                  type="text"
                  v-model="deviceData.LogicalDeviceNumber"
                  class="search-input"
                  placeholder="Enter LDN"
                  required
              />
            </div>
          </div>

          <div v-if="deviceData.IsIntegrated !== false" class="form-group">
            <div class="filter-label-container">
              <label class="filter-label">ID Radio</label>
              <div class="hint-icon" title="Radio module identifier">?</div>
            </div>
            <div class="search-input-container">
              <input
                  type="text"
                  v-model="deviceData.IDRadio"
                  class="search-input"
                  placeholder="Enter ID Radio"
              />
            </div>
          </div>

          <div class="form-group">
            <div class="filter-label-container">
              <label class="filter-label">Customer Number</label>
              <div class="hint-icon" title="Associated customer identifier">?</div>
            </div>
            <div class="search-input-container">
              <input
                  type="text"
                  v-model="deviceData.CustomerNumber"
                  class="search-input"
                  placeholder="Enter Customer Number"
              />
            </div>
          </div>

          <!-- LoRa EUI for BOTH meters AND addons with LORA protocols -->
          <div v-if="deviceData.Protocol === 'LORA' || deviceData.Protocol === 'LORA_WBUS'" class="form-group">
            <div class="filter-label-container">
              <label class="filter-label">LoRa EUI (DevEUI)</label>
            </div>
            <div class="search-input-container">
              <input
                  type="text"
                  v-model="deviceData.LoRaEUI"
                  class="search-input"
                  placeholder="e.g., 0123456789ABCDEF"
                  maxlength="16"
                  @input="deviceData.LoRaEUI = deviceData.LoRaEUI.toUpperCase()"
                  :required="deviceData.Protocol === 'LORA' || deviceData.Protocol === 'LORA_WBUS'"
              />
            </div>
          </div>

          <!-- Add MPID toggle in the Basic Information step (only for meters) -->
          <div v-if="deviceData.deviceCategory === 'meter'" class="form-group">
            <div class="filter-label-container">
              <label class="filter-label">Add MPID</label>
              <div class="hint-icon" title="Select if you want to add a Metering Point ID to this meter">?</div>
            </div>
            <div class="toggle-selection-wrapper">
              <button
                  :class="['toggle-option', { active: deviceData.addMpid === true }]"
                  @click="deviceData.addMpid = true"
              >
                Yes
              </button>
              <button
                  :class="['toggle-option', { active: deviceData.addMpid === false }]"
                  @click="deviceData.addMpid = false"
              >
                No
              </button>
            </div>
          </div>

          <!-- Add MPID input field if toggle is set to Yes -->
          <div v-if="deviceData.addMpid" class="form-group">
            <div class="filter-label-container">
              <label class="filter-label">MPID</label>
              <span class="required-asterisk">*</span>
              <div class="hint-icon" title="Metering Point Identifier">?</div>
            </div>
            <div class="search-input-container">
              <input
                  type="text"
                  v-model="deviceData.MPID"
                  class="search-input"
                  placeholder="Enter MPID"
                  :required="deviceData.addMpid"
              />
            </div>
          </div>
        </div>

        <!-- Step 3: Advanced Configuration -->
        <div v-if="currentStep === 2" class="form-step">
          <div v-if="deviceData.deviceCategory === 'meter'" class="form-group">
            <div class="filter-label-container">
              <label class="filter-label">Caliber</label>
              <div class="hint-icon" title="Meter caliber size in mm">?</div>
            </div>
            <div class="search-input-container">
              <input
                  type="text"
                  v-model="deviceData.Caliber"
                  class="search-input"
                  placeholder="Enter Caliber (e.g., 15, 20, 25)"
              />
            </div>
          </div>

          <!-- Protocol-specific key fields -->
          <div v-if="deviceData.Protocol === 'DLMS'" class="form-group">
            <div class="filter-label-container">
              <label class="filter-label">DLMS Key</label>
              <div class="hint-icon" title="Security key for DLMS communication">?</div>
            </div>
            <div class="search-input-container">
              <input
                  type="text"
                  v-model="deviceData.DLMSKey"
                  class="search-input"
                  placeholder="Enter DLMS Key"
              />
            </div>
          </div>

          <div v-if="deviceData.Protocol === 'LORA' || deviceData.Protocol === 'LORA_WBUS'" class="form-group">
            <div class="filter-label-container">
              <label class="filter-label">LoRa Application Key (AppKey)</label>
            </div>
            <div class="search-input-container">
              <input
                  type="text"
                  v-model="deviceData.LoRaKey"
                  class="search-input"
                  placeholder="e.g., 00112233445566778899AABBCCDDEEFF"
                  maxlength="32"
                  @input="deviceData.LoRaKey = deviceData.LoRaKey.toUpperCase()"
                  :required="deviceData.Protocol === 'LORA' || deviceData.Protocol === 'LORA_WBUS'"
              />
            </div>

          </div>

          <div v-if="deviceData.Protocol === 'LORA' || deviceData.Protocol === 'LORA_WBUS'" class="form-group">
            <div class="filter-label-container">
              <label class="filter-label">LoRa Network Key (NwkKey)</label>
            </div>
            <div class="search-input-container">
              <input
                  type="text"
                  v-model="deviceData.LoRaNTWKey"
                  class="search-input"
                  placeholder="e.g., 00112233445566778899AABBCCDDEEFF (optional)"
                  maxlength="32"
                  @input="deviceData.LoRaNTWKey = deviceData.LoRaNTWKey ? deviceData.LoRaNTWKey.toUpperCase() : ''"
              />
            </div>
          </div>

          <div v-if="deviceData.Protocol === 'WBUS' || deviceData.Protocol === 'LORA_WBUS'" class="form-group">
            <div class="filter-label-container">
              <label class="filter-label">WBus Key</label>
              <div class="hint-icon" title="Security key for WBus communication">?</div>
            </div>
            <div class="search-input-container">
              <input
                  type="text"
                  v-model="deviceData.WBusKey"
                  class="search-input"
                  placeholder="Enter WBus Key"
              />
            </div>
          </div>

          <div v-if="deviceData.Protocol === 'CTR'" class="form-group">
            <div class="filter-label-container">
              <label class="filter-label">CTR Key</label>
              <span class="required-asterisk">*</span>
              <div class="hint-icon" title="Security key for CTR communication">?</div>
            </div>
            <div class="search-input-container">
              <input
                  type="text"
                  v-model="deviceData.CTRKey"
                  class="search-input"
                  placeholder="Enter CTR Key"
                  :required="deviceData.Protocol === 'CTR'"
              />
            </div>
          </div>
        </div>

        <!-- Step 4: Confirmation -->
        <div v-if="currentStep === 3" class="form-step">
          <h3 class="confirmation-title">Device Summary</h3>
          <div class="confirmation-list">
            <div v-if="deviceData.deviceCategory" class="confirmation-item">
              <span class="confirmation-label">Device Type:</span>
              <span class="confirmation-value">{{ deviceData.deviceCategory === 'meter' ? 'Meter' : 'Add-on Module' }}</span>
            </div>
            <div v-if="deviceData.IsIntegrated !== undefined" class="confirmation-item">
              <span class="confirmation-label">Integrated:</span>
              <span class="confirmation-value">{{ deviceData.IsIntegrated ? 'Yes' : 'No' }}</span>
            </div>
            <div v-if="deviceData.Protocol" class="confirmation-item">
              <span class="confirmation-label">Protocol:</span>
              <span class="confirmation-value">{{ deviceData.Protocol }}</span>
            </div>
            <div v-if="deviceData.LogicalDeviceNumber" class="confirmation-item">
              <span class="confirmation-label">LDN:</span>
              <span class="confirmation-value">{{ deviceData.LogicalDeviceNumber }}</span>
            </div>
            <div v-if="deviceData.IDRadio" class="confirmation-item">
              <span class="confirmation-label">ID Radio:</span>
              <span class="confirmation-value">{{ deviceData.IDRadio }}</span>
            </div>
            <div v-if="deviceData.CustomerNumber" class="confirmation-item">
              <span class="confirmation-label">Customer Number:</span>
              <span class="confirmation-value">{{ deviceData.CustomerNumber }}</span>
            </div>
            <div v-if="deviceData.LoRaEUI" class="confirmation-item">
              <span class="confirmation-label">LoRa EUI:</span>
              <span class="confirmation-value">{{ deviceData.LoRaEUI }}</span>
            </div>
            <div v-if="deviceData.MPID" class="confirmation-item">
              <span class="confirmation-label">MPID:</span>
              <span class="confirmation-value">{{ deviceData.MPID }}</span>
            </div>
            <div v-if="deviceData.Caliber" class="confirmation-item">
              <span class="confirmation-label">Caliber:</span>
              <span class="confirmation-value">{{ deviceData.Caliber }} mm</span>
            </div>
            <div v-if="deviceData.DLMSKey" class="confirmation-item">
              <span class="confirmation-label">DLMS Key:</span>
              <span class="confirmation-value">{{ deviceData.DLMSKey }}</span>
            </div>
            <div v-if="deviceData.LoRaKey" class="confirmation-item">
              <span class="confirmation-label">LoRa Key:</span>
              <span class="confirmation-value">{{ deviceData.LoRaKey }}</span>
            </div>
            <div v-if="deviceData.LoRaNTWKey" class="confirmation-item">
              <span class="confirmation-label">LoRa Network Key:</span>
              <span class="confirmation-value">{{ deviceData.LoRaNTWKey }}</span>
            </div>
            <div v-if="deviceData.WBusKey" class="confirmation-item">
              <span class="confirmation-label">WBus Key:</span>
              <span class="confirmation-value">{{ deviceData.WBusKey }}</span>
            </div>
            <div v-if="deviceData.CTRKey" class="confirmation-item">
              <span class="confirmation-label">CTR Key:</span>
              <span class="confirmation-value">{{ deviceData.CTRKey }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Navigation buttons -->
      <div class="filter-actions">
        <button v-if="currentStep > 0" class="action-button" @click="prevStep">
          Back
        </button>
        <button class="action-button" @click="close">Cancel</button>
        <button
            v-if="currentStep < steps.length - 1"
            class="action-button"
            @click="nextStep"
            :disabled="!canProceed"
        >
          Next
        </button>
        <button
            v-else
            class="action-button submit-button"
            @click="submitDevice"
        >
          Add Device
        </button>
      </div>
    </div>
  </div>
</template>
<script>
export default {
  name: 'AddDeviceOverlay',
  props: {
    isVisible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      currentStep: 0,
      steps: ['Device Type', 'Basic Information', 'Configuration', 'Confirmation'],
      deviceData: {
        // UI-specific fields
        deviceCategory: '', // 'meter' or 'addon'
        addMpid: false, // New flag to control MPID input

        // Model fields
        tenant_id: '', // This would be set by the system
        MPID: '',
        LogicalDeviceNumber: '',
        IDRadio: '',
        LoRaEUI: '',
        CustomerNumber: '',
        Type: '',
        IsIntegrated: undefined,
        Protocol: '',
        LoRaKey: '',
        LoRaNTWKey: '',
        WBusKey: '',
        DLMSKey: '',
        CTRKey: '',
        Caliber: '',
      }
    };
  },
  computed: {
    canProceed() {
      switch (this.currentStep) {
        case 0:
          // First step validation: Device type selection
          if (!this.deviceData.deviceCategory) return false;

          // If meter is selected, check if integrated status is defined
          if (this.deviceData.deviceCategory === 'meter' && this.deviceData.IsIntegrated === undefined) {
            return false;
          }

          // If integrated meter, check if protocol is selected
          if (this.deviceData.deviceCategory === 'meter' &&
              this.deviceData.IsIntegrated === true &&
              !this.deviceData.Protocol) {
            return false;
          }

          // If addon module, check if protocol is selected
          if (this.deviceData.deviceCategory === 'addon' && !this.deviceData.Protocol) {
            return false;
          }

          return true;

        case 1:
          // Second step validation: Basic information
          if (!this.deviceData.LogicalDeviceNumber) return false;

          // For LoRa devices (both meter and addon), require LoRaEUI
          if ((this.deviceData.Protocol === 'LORA' || this.deviceData.Protocol === 'LORA_WBUS') &&
              !this.deviceData.LoRaEUI) {
            return false;
          }

          // Validate LoRaEUI format if provided (16 hex characters)
          if (this.deviceData.LoRaEUI && !/^[0-9A-Fa-f]{16}$/.test(this.deviceData.LoRaEUI)) {
            return false;
          }

          // If it's a meter, check that addMpid has been set (either true or false)
          if (this.deviceData.deviceCategory === 'meter' && this.deviceData.addMpid === undefined) {
            return false;
          }

          // If addMpid is true, validate MPID is provided
          if (this.deviceData.addMpid && !this.deviceData.MPID) return false;

          return true;

        case 2:
          // Third step validation: Configuration keys

          // Check if CTR protocol requires a key
          if (this.deviceData.Protocol === 'CTR' && !this.deviceData.CTRKey) {
            return false;
          }

          // Check if LORA protocol requires LoRaKey
          if ((this.deviceData.Protocol === 'LORA' || this.deviceData.Protocol === 'LORA_WBUS') &&
              !this.deviceData.LoRaKey) {
            return false;
          }

          // Validate LoRaKey format if provided (32 hex characters)
          if (this.deviceData.LoRaKey && !/^[0-9A-Fa-f]{32}$/.test(this.deviceData.LoRaKey)) {
            return false;
          }

          // Validate LoRaNTWKey format if provided (32 hex characters)
          if (this.deviceData.LoRaNTWKey && !/^[0-9A-Fa-f]{32}$/.test(this.deviceData.LoRaNTWKey)) {
            return false;
          }

          return true;

        default:
          return true;
      }
    }
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        document.body.style.overflow = 'hidden'; // Prevent scrolling when overlay is open
        // Reset form data when opening
        this.resetForm();
      } else {
        document.body.style.overflow = ''; // Restore scrolling when overlay is closed
      }
    },
    'deviceData.deviceCategory'(newVal) {
      // Update Type field based on category
      if (newVal === 'meter') {
        this.deviceData.Type = 'METER';
        this.deviceData.IsIntegrated = undefined; // Reset integrated status for meter
        this.deviceData.addMpid = false; // Default to not adding MPID
      } else if (newVal === 'addon') {
        this.deviceData.Type = 'ADDON';
        this.deviceData.IsIntegrated = false; // Addons are always not integrated
        this.deviceData.addMpid = false; // Addons don't use MPID directly
      }
    },
    'deviceData.IsIntegrated'(newVal) {
      if (newVal === true) {
        // For integrated meters, Type should be METER
        this.deviceData.Type = 'METER';
        // Protocol will be set when selected
      } else if (newVal === false) {
        // For non-integrated meters, type is MECHANICAL
        this.deviceData.Type = 'MECHANICAL';
        this.deviceData.Protocol = 'NONE';
      }
    },
    'deviceData.Protocol'(newVal) {
      if (this.deviceData.deviceCategory === 'meter' && this.deviceData.IsIntegrated && newVal) {
        // For integrated meters, Type should remain METER
        this.deviceData.Type = 'METER';
      } else if (this.deviceData.deviceCategory === 'addon' && newVal) {
        // For addon modules, Type should always be ADDON regardless of protocol
        this.deviceData.Type = 'ADDON';
      }

      // Clear protocol-specific keys when changing protocols
      if (newVal !== 'DLMS') this.deviceData.DLMSKey = '';
      if (newVal !== 'LORA' && newVal !== 'LORA_WBUS') {
        this.deviceData.LoRaKey = '';
        this.deviceData.LoRaNTWKey = '';
        this.deviceData.LoRaEUI = '';
      }
      if (newVal !== 'WBUS' && newVal !== 'LORA_WBUS') this.deviceData.WBusKey = '';
      if (newVal !== 'CTR') this.deviceData.CTRKey = '';
    },
    // Clear MPID if addMpid is set to false
    'deviceData.addMpid'(newVal) {
      if (newVal === false) {
        this.deviceData.MPID = '';
      }
    }
  },
  methods: {
    resetForm() {
      this.currentStep = 0;
      this.deviceData = {
        deviceCategory: '',
        addMpid: false,
        tenant_id: '',
        MPID: '',
        LogicalDeviceNumber: '',
        IDRadio: '',
        LoRaEUI: '',
        CustomerNumber: '',
        Type: '',
        IsIntegrated: undefined,
        Protocol: '',
        LoRaKey: '',
        LoRaNTWKey: '',
        WBusKey: '',
        DLMSKey: '',
        CTRKey: '',
        Caliber: '',
      };
    },
    nextStep() {
      if (this.currentStep < this.steps.length - 1 && this.canProceed) {
        this.currentStep++;
      }
    },
    prevStep() {
      if (this.currentStep > 0) {
        this.currentStep--;
      }
    },
    close() {
      this.$emit('close');
    },
    submitDevice() {
      // Prepare final data for submission
      const submitData = { ...this.deviceData };

      // Remove UI-specific fields
      delete submitData.deviceCategory;
      delete submitData.addMpid;

      // Set the installation date to current time
      submitData.Installed = new Date().toISOString();

      // Make sure protocol is set for non-integrated devices
      if (submitData.IsIntegrated === false && (!submitData.Protocol || submitData.Protocol === '')) {
        submitData.Protocol = 'NONE';
      }

      console.log('Submitting device data:', submitData);

      // Emit event with device data for parent component to handle
      this.$emit('submit', submitData);

      // Close the overlay
      this.close();
    }
  }
};
</script>

<style scoped>
/* Overlay */
.filter-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.filter-overlay-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to top, rgba(32, 32, 32, 0.1) 0%, rgba(16, 16, 16, 0.95) 100%);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(4px);
  animation: fadeIn 0.3s ease forwards;
}

.filter-overlay-content {
  position: relative;
  width: 600px;
  max-width: 90%;
  max-height: 90vh;
  border-radius: 8px;
  padding: 24px;
  color: white;
  overflow-y: auto;
  animation: slideIn 0.3s ease forwards;
  z-index: 1001;
}

/* Header */
.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 16px;
}

.filter-header h2 {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin: 0;
}

/* Carousel/Steps Navigation */
.carousel-navigation {
  display: flex;
  justify-content: space-between;
  margin-bottom: 32px;
  position: relative;
}

.carousel-navigation::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  z-index: 1;
}

.step-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
}

.step-number {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
  border: 2px solid transparent;
  transition: all 0.3s ease;
}

.step-name {
  font-size: 10px;
  font-weight: 500;
  opacity: 0.7;
  text-align: center;
  transition: opacity 0.3s ease;
}

.step-indicator.active .step-number {
  background-color: rgba(79, 195, 247, 0.2);
  border-color: #4fc3f7;
  color: #4fc3f7;
}

.step-indicator.active .step-name {
  opacity: 1;
  color: #4fc3f7;
}

.step-indicator.completed .step-number {
  background-color: rgba(79, 195, 247, 0.5);
  border-color: #4fc3f7;
}

/* Form Steps */
.form-step {
  animation: fadeIn 0.3s ease forwards;
}

.form-group {
  margin-bottom: 20px;
}

.filter-label-container {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.filter-label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  opacity: 0.9;
}

.hint-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: rgba(79, 195, 247, 0.3);
  color: #4fc3f7;
  font-size: 7px;
  font-weight: bold;
  cursor: help;
  transition: background-color 0.2s, color 0.2s;
}

.hint-icon:hover {
  background-color: rgba(79, 195, 247, 0.6);
  color: white;
}

/* Input Fields */
.search-input-container {
  position: relative;
  max-width: 100%;
  padding-left: 25px;
}

.search-input {
  width: 100%;
  background: transparent;
  border: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 8px 0;
  font-size: 12px;
}

.search-input:focus {
  outline: none;
  border-bottom-color: rgba(255, 255, 255, 0.6);
}

.search-input::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

/* Toggle selection style */
.toggle-selection-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 8px;
}

.toggle-option {
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: pointer;
  padding: 0;
  opacity: 0.4;
  transition: opacity 0.2s;
}

.toggle-option:hover,
.toggle-option.active {
  opacity: 1;
}

.toggle-option.active {
  position: relative;
}

.toggle-option.active::after {
  content: '';
  position: absolute;
  bottom: -5px;
  left: 0;
  width: 100%;
  height: 2px;
  background-color: #4fc3f7;
  opacity: 0.7;
}

/* Confirmation Step */
.confirmation-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 20px;
  opacity: 0.9;
}

.confirmation-list {
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 16px;
}

.confirmation-item {
  display: flex;
  margin-bottom: 12px;
  font-size: 14px;
}

.confirmation-label {
  flex: 0 0 40%;
  font-weight: 500;
  opacity: 0.7;
}

.confirmation-value {
  flex: 0 0 60%;
  font-weight: 400;
}

/* Action buttons */
.filter-actions {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 24px;
  padding-top: 20px;
}

.action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.action-button:hover:not(:disabled) {
  background-color: rgba(255, 255, 255, 0.1);
}

.action-button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.submit-button {
  background-color: rgba(79, 195, 247, 0.2);
}

.submit-button:hover {
  background-color: rgba(79, 195, 247, 0.3);
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
